'use client'

import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Checkbox } from '@/components/ui/checkbox'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import type { IntegrationProvider } from '@/lib/integrations-constants'

// ============================================================================
// Types & Schema
// ============================================================================

const paymentMethodSchema = z.object({
  enableExpressCheckout: z.boolean(),
  enablePaymentFormCheckout: z.boolean(),
  country: z.string().optional(),
  sandboxMode: z.boolean(),
})

type FormValues = z.infer<typeof paymentMethodSchema>

interface PaymentMethodFormProps {
  provider: IntegrationProvider
  defaultValues?: Record<string, any>
  onSubmit: (data: Record<string, any>) => Promise<void>
  isLoading?: boolean
}

// ============================================================================
// Component
// ============================================================================

export function PaymentMethodForm({
  provider,
  defaultValues = {},
  onSubmit,
  isLoading = false,
}: PaymentMethodFormProps) {
  const form = useForm<FormValues>({
    resolver: zodResolver(paymentMethodSchema),
    defaultValues: {
      enableExpressCheckout: defaultValues.metadata?.enableExpressCheckout ?? true,
      enablePaymentFormCheckout: defaultValues.metadata?.enablePaymentFormCheckout ?? false,
      country: defaultValues.metadata?.country ?? 'US',
      sandboxMode: defaultValues.metadata?.sandboxMode ?? false,
    },
  })

  const { handleSubmit, watch, setValue } = form

  const handleFormSubmit = async (data: FormValues) => {
    await onSubmit({
      metadata: data,
    })
  }

  // Configuration options based on provider
  const getProviderConfig = () => {
    switch (provider) {
      case 'apple_pay':
        return {
          title: 'Apple Pay Configuration',
          description: 'Configure Apple Pay settings for your checkout',
          features: [
            {
              id: 'enableExpressCheckout',
              label: 'Enable Express Checkout',
              description: 'Show Apple Pay button on product pages for quick checkout',
            },
            {
              id: 'enablePaymentFormCheckout',
              label: 'Enable Payment Form Checkout',
              description: 'Show Apple Pay as a payment option in the checkout form',
            },
          ],
          requiresDomainVerification: true,
        }
      case 'google_pay':
        return {
          title: 'Google Pay Configuration',
          description: 'Configure Google Pay settings for your checkout',
          features: [
            {
              id: 'enableExpressCheckout',
              label: 'Enable Express Checkout',
              description: 'Show Google Pay button on product pages for quick checkout',
            },
            {
              id: 'enablePaymentFormCheckout',
              label: 'Enable Payment Form Checkout',
              description: 'Show Google Pay as a payment option in the checkout form',
            },
          ],
          requiresDomainVerification: false,
        }
      default:
        return {
          title: 'Payment Method Configuration',
          description: 'Configure payment settings',
          features: [],
          requiresDomainVerification: false,
        }
    }
  }

  const config = getProviderConfig()

  return (
    <form id="integration-config-form" onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">{/* Domain Verification Warning for Apple Pay */}
      {config.requiresDomainVerification && (
        <Card className="border-orange-200 bg-orange-50">
          <CardHeader>
            <CardTitle className="text-base text-orange-900">Domain Verification Required</CardTitle>
            <CardDescription className="text-orange-700">
              Apple Pay requires domain verification before it can be enabled.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ol className="list-inside list-decimal space-y-2 text-sm text-orange-800">
              <li>Set up a custom domain for your checkout page</li>
              <li>Verify the domain with Apple through our integration settings</li>
              <li>Enable Apple Pay after verification is complete</li>
            </ol>
          </CardContent>
        </Card>
      )}

      {/* Configuration Options */}
      {config.features.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>{config.title}</CardTitle>
            <CardDescription>{config.description}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {config.features.map((feature) => (
              <div key={feature.id} className="flex items-start space-x-3">
                <Checkbox
                  id={feature.id}
                  checked={watch(feature.id as keyof FormValues) as boolean}
                  onCheckedChange={(checked) => setValue(feature.id as keyof FormValues, checked as boolean)}
                  disabled={isLoading}
                  className="mt-1"
                />
                <div className="space-y-1">
                  <Label htmlFor={feature.id} className="cursor-pointer font-medium">
                    {feature.label}
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Sandbox Mode */}
      <Card>
        <CardHeader>
          <CardTitle>Testing & Development</CardTitle>
          <CardDescription>
            Configure test mode for development and staging environments
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="sandboxMode" className="font-medium">Sandbox Mode</Label>
              <p className="text-sm text-muted-foreground">
                Use test credentials for development and testing
              </p>
            </div>
            <Switch
              id="sandboxMode"
              checked={watch('sandboxMode')}
              onCheckedChange={(checked) => setValue('sandboxMode', checked)}
              disabled={isLoading}
            />
          </div>
        </CardContent>
      </Card>

      {/* Information Card */}
      <Card>
        <CardHeader>
          <CardTitle>About {provider === 'apple_pay' ? 'Apple Pay' : 'Google Pay'}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-muted-foreground">
          {provider === 'apple_pay' ? (
            <>
              <p>
                Apple Pay is available on Safari on macOS, iOS, and iPadOS devices.
                Customers can complete their purchase with a single touch using Face ID or Touch ID.
              </p>
              <p>
                <strong>Requirements:</strong>
              </p>
              <ul className="list-inside list-disc space-y-1 pl-4">
                <li>Your checkout page must use a verified custom domain with HTTPS</li>
                <li>Customers must have Apple Pay enabled on their device</li>
                <li>Customers must be using Safari browser</li>
              </ul>
            </>
          ) : (
            <>
              <p>
                Google Pay is available on Chrome browsers and Android devices.
                Customers can complete their purchase quickly using saved payment methods.
              </p>
              <p>
                <strong>Requirements:</strong>
              </p>
              <ul className="list-inside list-disc space-y-1 pl-4">
                <li>Your checkout page must use HTTPS</li>
                <li>Customers must have a Google account with saved payment methods</li>
                <li>Works on Chrome, Edge, and other Chromium-based browsers</li>
              </ul>
            </>
          )}
        </CardContent>
      </Card>
    </form>
  )
}
